import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { canApproveAgentCommand, canReadCommandOutput } from '@infras/shared-types';
import { useMe } from '../lib/useMe';
import { useCrumb } from '../lib/crumb';
import { DetailHead } from '../components/DetailHead';
import { DetailBody, RailCard } from '../components/DetailRail';
import {
  AGENT_COMMAND,
  AGENT_COMMAND_OUTPUT,
  APPROVE_AGENT_COMMAND,
  CANCEL_AGENT_COMMAND,
  CMD_STATUS_CLASS,
  IN_FLIGHT,
  OP_LABEL,
  REJECT_AGENT_COMMAND,
  isPlanConflict,
  prettyJson,
  serverMessage,
  timeAgo,
  type AgentCommand,
  type AgentCommandEvent,
  type ClusterAgentSettings,
} from '../lib/agentCommands';
import { RefreshButton } from '../components/RefreshButton';

/**
 * One command: the FROZEN approved plan, its lifecycle timeline, and — behind an
 * explicit click, never on load — the captured output, which can contain pod
 * logs and is therefore root|maintainer only on its own query.
 *
 * A draft can still be approved from here; approval sends the row's own
 * `planDigest` (the value frozen at draft time), not a fresh preview digest.
 */

interface Data {
  agentCommand: AgentCommand | null;
  agentCommandEvents: AgentCommandEvent[];
  clusterAgentSettings: ClusterAgentSettings[];
}

export function AgentCommandPage() {
  const { id = '' } = useParams();
  const { me } = useMe();
  const role = me?.role ?? 'new_member';

  const { data, loading, error, refetch } = useQuery<Data>(AGENT_COMMAND, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    pollInterval: 4000,
  });

  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [approveCmd] = useMutation(APPROVE_AGENT_COMMAND);
  const [rejectCmd] = useMutation(REJECT_AGENT_COMMAND);
  const [cancelCmd] = useMutation(CANCEL_AGENT_COMMAND);

  const [loadOutput, outputQ] = useLazyQuery<{ agentCommandOutput: string | null }>(AGENT_COMMAND_OUTPUT, {
    fetchPolicy: 'network-only',
  });

  // MUST sit above the early returns — called after them it is a conditional
  // hook, which is how four other detail pages were quietly broken.
  useCrumb(data?.agentCommand ? `#${data.agentCommand.id}` : undefined);

  if (loading && !data) return <div className="page">Loading…</div>;
  if (error)
    return (
      <div className="page">
        <p className="error">Couldn&apos;t load this command — the API didn&apos;t respond.</p>
        <p className="muted">
          If it was already approved it keeps running; agents claim from the server, not from this
          page. Retry, then ask in #devops-infra.
        </p>
      </div>
    );

  const cmd = data?.agentCommand;
  if (!cmd)
    return (
      <div className="page">
        <p className="muted">Command “{id}” not found.</p>
        <Link to="/cluster-access/commands">← Back to commands</Link>
      </div>
    );

  const events = data?.agentCommandEvents ?? [];
  const isDraft = cmd.status === 'draft';
  const inFlight = IN_FLIGHT.includes(cmd.status);
  // `production` lives on the cluster, not the row — default to the strict side
  // when the cluster has no settings row (which is also how the server plans it).
  const settings = (data?.clusterAgentSettings ?? []).find((s) => s.clusterName === cmd.clusterName);
  const production = settings?.isProduction ?? true;
  const canApprove = canApproveAgentCommand(role, { mutating: cmd.mutating, production });
  const needsTyped = cmd.mutating && production;
  const typedOk = !needsTyped || typed.trim() === cmd.clusterName;

  async function run(fn: () => Promise<unknown>) {
    setErr(null);
    setBusy(true);
    try {
      await fn();
      await refetch();
    } catch (e) {
      setErr(serverMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // Rail contents. Split in two because they answer different questions: WHAT
  // this command does (fixed at draft) and WHERE it has got to (moves while you
  // watch). Identifiers render in Plex Sans — the phase 6 §1 mono policy; only
  // the args JSON below is genuinely code.
  const what: [string, ReactNode][] = [
    ['Operation', cmd.op],
    ['Cluster', cmd.clusterName],
    ['Target', cmd.agentId ?? cmd.target],
    ['Mutating', cmd.mutating ? 'yes' : 'no'],
  ];
  const lifecycle: [string, ReactNode][] = [
    ['Requested by', cmd.requestedBy],
    ['Approved by', cmd.approvedBy ?? '—'],
    ['Approved at', cmd.approvedAt ? new Date(cmd.approvedAt).toLocaleString() : '—'],
    ['Claimed by', cmd.claimedByAgentId ?? '—'],
    ['Attempt', String(cmd.attempt)],
    ['Started', cmd.startedAt ? new Date(cmd.startedAt).toLocaleString() : '—'],
    ['Finished', cmd.finishedAt ? new Date(cmd.finishedAt).toLocaleString() : '—'],
    ['Exit code', cmd.exitCode === null || cmd.exitCode === undefined ? '—' : String(cmd.exitCode)],
    ['Verified', cmd.verifiedAt ? new Date(cmd.verifiedAt).toLocaleString() : '—'],
    ['Expires', new Date(cmd.expiresAt).toLocaleString()],
  ];

  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/cluster-access/commands" className="muted">
          ← Agent commands
        </Link>
      </div>
      <DetailHead
        name={OP_LABEL[cmd.op] ?? cmd.op}
        badge={
          <>
            <span className={`badge ${CMD_STATUS_CLASS[cmd.status] ?? ''}`}>{cmd.status}</span>
            {cmd.mutating && <span className="badge inferred">mutating</span>}
            {/* `failed`, not `prod`. This borrowed the production pill purely for its red;
                now that production is green, it needs the red class by name. Same pixels
                as before — `.badge.failed` is the same red. */}
            {cmd.cancelRequested && <span className="badge failed">cancel requested</span>}
          </>
        }
        facts={[
          `#${cmd.id}`,
          cmd.clusterName,
          cmd.agentId ?? cmd.target,
          `by ${cmd.requestedBy}`,
          cmd.commandId,
        ]}
        curated
        // A command isn't scanned — its age is its own lifecycle, and the page
        // polls, so "last scanned" would be the wrong sentence entirely.
        scanLead={`Created ${timeAgo(cmd.createdAt)}. This page refreshes every few seconds while the command is in flight.`}
        actions={
          <>
            {(isDraft || inFlight) && (
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => run(() => cancelCmd({ variables: { id, note: note.trim() || null } }))}
              >
                Cancel
              </button>
            )}
            {isDraft && canApprove && (
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => run(() => rejectCmd({ variables: { id, note: note.trim() || null } }))}
              >
                Reject
              </button>
            )}
          </>
        }
      />

      <DetailBody
        rail={
          <>
            <RailCard title="Command">
              <RailFacts rows={what} />
            </RailCard>
            <RailCard title="Lifecycle">
              <RailFacts rows={lifecycle} />
            </RailCard>
            <RailCard title="Elsewhere">
              <div className="stack-sm">
                <Link to={`/cluster-access/fleet?cluster=${encodeURIComponent(cmd.clusterName)}`}>
                  Agents on this cluster
                </Link>
                <Link to={`/cluster-access/commands?cluster=${encodeURIComponent(cmd.clusterName)}`}>
                  Other commands here
                </Link>
              </div>
            </RailCard>
          </>
        }
      >

      {err && (
        <p className="run-error">
          {err}
          {isPlanConflict(err) && (
            <>
              <br />
              The plan changed since it was drafted — review the refreshed diff and issue a new command.
            </>
          )}
        </p>
      )}

      {isDraft && (
        <div className="dep-card cmd-approve">
          <strong>Awaiting approval.</strong>{' '}
          {canApprove ? (
            <>
              Approving dispatches <em>exactly</em> the frozen plan below.
              {needsTyped && (
                <label className="cmd-field cmd-confirm">
                  <span className="cmd-field-k">
                    Type <strong>{cmd.clusterName}</strong> to confirm
                  </span>
                  <input
                    className="class-select"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoComplete="off"
                  />
                </label>
              )}
              <label className="cmd-field">
                <span className="cmd-field-k">Note (optional)</span>
                <input className="class-select" value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
              <div className="btn-row">
                <button
                  type="button"
                  className={cmd.mutating ? 'btn btn-danger' : 'btn btn-primary'}
                  disabled={busy || !typedOk || !cmd.planDigest}
                  onClick={() =>
                    run(() =>
                      // The DRAFT's own digest — a fresh preview would legitimately
                      // differ (server-generated values are frozen at draft time).
                      approveCmd({
                        variables: { id, planDigest: cmd.planDigest, note: note.trim() || null },
                      }),
                    )
                  }
                >
                  {busy ? 'Approving…' : 'Approve & dispatch'}
                </button>
              </div>
            </>
          ) : (
            <>A mutating operation on a production cluster requires root approval.</>
          )}
        </div>
      )}

      <h3 className="section">Plan (frozen at draft)</h3>
      {cmd.planSummary && <div className="cmd-summary">{cmd.planSummary}</div>}
      <div className="log-viewer">
        <pre>{cmd.planDiff?.trim() || '(no diff recorded)'}</pre>
      </div>
      <div className="kv" style={{ marginTop: 10 }}>
        <div className="kv-k">Plan digest</div>
        <div className="kv-v">
          <code className="text-xs">{cmd.planDigest ?? '—'}</code>
        </div>
      </div>

      <h3 className="section">Args</h3>
      {/* Genuinely code — raw JSON, one of the three uses the mono policy keeps. */}
      <div className="log-viewer">
        <pre>{prettyJson(cmd.args)}</pre>
      </div>
      {cmd.error && <div className="run-error" style={{ marginTop: 10 }}>{cmd.error}</div>}

      <h3 className="section">Result</h3>
      <div className="log-viewer">
        <pre>{prettyJson(cmd.result)}</pre>
      </div>

      <h3 className="section">Output</h3>
      {!canReadCommandOutput(role) ? (
        <p className="muted">
          Command output can contain pod logs — root or maintainer only.
        </p>
      ) : outputQ.data || outputQ.error ? (
        outputQ.error ? (
          <p className="run-error">{serverMessage(outputQ.error)}</p>
        ) : (
          <div className="log-viewer">
            <pre>{outputQ.data?.agentCommandOutput?.trim() || '(no output captured)'}</pre>
          </div>
        )
      ) : (
        <p className="muted">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={outputQ.loading}
            onClick={() => loadOutput({ variables: { id } })}
          >
            {outputQ.loading ? 'Loading…' : 'Load output'}
          </button>{' '}
          Not fetched automatically — it can contain pod logs.
        </p>
      )}

      <div className="section-head">
        <h3>Timeline ({events.length})</h3>
        <span className="spacer" />
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>
      {!events.length ? (
        <p className="muted">No events recorded.</p>
      ) : (
        <table className="mtx">
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>Actor</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td className="muted">{new Date(e.createdAt).toLocaleString()}</td>
                <td>
                  <span className="badge quiet">{e.kind}</span>
                </td>
                <td className="muted">{e.actor}</td>
                <td className="muted text-2xs">
                  {e.note || ''}
                  {e.meta && e.meta !== '{}' && <div><code>{e.meta}</code></div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </DetailBody>
    </div>
  );
}

/** Label/value stack for a 300px rail card — the kv-grid's two columns don't fit
 *  there, and a timestamp wrapping mid-value reads as two rows. */
function RailFacts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <div className="stack-sm">
      {rows.map(([k, v]) => (
        <div key={k}>
          <div className="kv-k">{k}</div>
          <div className="kv-v text-xs">{v}</div>
        </div>
      ))}
    </div>
  );
}
